import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Calendar,
  Save,
  Database,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate months for a financial year
const generateFYMonths = (fy) => {
  const [startYear] = fy.split('-').map(y => parseInt(y.length === 2 ? `20${y}` : y));
  return [
    { month: '04', label: 'April', year: startYear },
    { month: '05', label: 'May', year: startYear },
    { month: '06', label: 'June', year: startYear },
    { month: '07', label: 'July', year: startYear },
    { month: '08', label: 'August', year: startYear },
    { month: '09', label: 'September', year: startYear },
    { month: '10', label: 'October', year: startYear },
    { month: '11', label: 'November', year: startYear },
    { month: '12', label: 'December', year: startYear },
    { month: '01', label: 'January', year: startYear + 1 },
    { month: '02', label: 'February', year: startYear + 1 },
    { month: '03', label: 'March', year: startYear + 1 },
  ];
};

const HistoricalDataPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { selectedFY } = useFinancialYear();
  const FY_MONTHS = useMemo(() => generateFYMonths(selectedFY), [selectedFY]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cutoffDate, setCutoffDate] = useState('');
  const [entries, setEntries] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [tempCutoffDate, setTempCutoffDate] = useState('');

  const canEdit = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';

  // Fetch settings and entries
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch settings
      const settingsRes = await axios.get(`${API}/historical/settings`, {
        headers: getAuthHeaders()
      });
      setCutoffDate(settingsRes.data.cutoff_date || '');
      
      // Fetch entries
      const entriesRes = await axios.get(`${API}/historical/entries/${selectedFY}`, {
        headers: getAuthHeaders()
      });
      
      // Convert to lookup object
      const entriesMap = {};
      entriesRes.data.forEach(entry => {
        const key = `${entry.year}-${entry.month}`;
        entriesMap[key] = {
          training_expenses: entry.training_expenses || 0,
          hr_salary_expenses: entry.hr_salary_expenses || 0,
          hr_other_expenses: entry.hr_other_expenses || 0
        };
      });
      
      // Initialize all months
      FY_MONTHS.forEach(m => {
        const key = `${m.year}-${m.month}`;
        if (!entriesMap[key]) {
          entriesMap[key] = {
            training_expenses: 0,
            hr_salary_expenses: 0,
            hr_other_expenses: 0
          };
        }
      });
      
      setEntries(entriesMap);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch historical data:', error);
      toast.error('Failed to load historical data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedFY, FY_MONTHS]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update entry value
  const updateEntry = (monthKey, field, value) => {
    setEntries(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        [field]: parseFloat(value) || 0
      }
    }));
    setHasChanges(true);
  };

  // Check if month is before cutoff
  const isBeforeCutoff = (month, year) => {
    if (!cutoffDate) return false;
    const monthEnd = `${year}-${month}-${month === '02' ? '28' : ['04', '06', '09', '11'].includes(month) ? '30' : '31'}`;
    return monthEnd < cutoffDate;
  };

  // Check if month contains cutoff
  const isPartialMonth = (month, year) => {
    if (!cutoffDate) return false;
    const [cutoffYear, cutoffMonth] = cutoffDate.split('-');
    return cutoffMonth === month && parseInt(cutoffYear) === year;
  };

  // Save cutoff date
  const saveCutoffDate = async () => {
    try {
      await axios.post(`${API}/historical/settings`, 
        { cutoff_date: tempCutoffDate },
        { headers: getAuthHeaders() }
      );
      setCutoffDate(tempCutoffDate);
      setSettingsModalOpen(false);
      toast.success('Cutoff date updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cutoff date');
    }
  };

  // Save all entries
  const saveEntries = async () => {
    if (!cutoffDate) {
      toast.error('Please set a cutoff date first');
      setSettingsModalOpen(true);
      return;
    }

    try {
      setSaving(true);
      
      // Prepare entries to save (only months before or including cutoff)
      const entriesToSave = [];
      
      FY_MONTHS.forEach(m => {
        const key = `${m.year}-${m.month}`;
        const entry = entries[key];
        
        // Only save if month is before or contains cutoff
        if (isBeforeCutoff(m.month, m.year) || isPartialMonth(m.month, m.year)) {
          entriesToSave.push({
            financial_year: selectedFY,
            month: m.month,
            year: m.year,
            training_expenses: entry.training_expenses,
            hr_salary_expenses: entry.hr_salary_expenses,
            hr_other_expenses: entry.hr_other_expenses
          });
        }
      });
      
      await axios.post(`${API}/historical/entries/bulk`, entriesToSave, {
        headers: getAuthHeaders()
      });
      
      setHasChanges(false);
      toast.success('Historical data saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save historical data');
    } finally {
      setSaving(false);
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate totals
  const calculateTotals = () => {
    let training = 0, hrSalary = 0, hrOther = 0;
    
    FY_MONTHS.forEach(m => {
      const key = `${m.year}-${m.month}`;
      const entry = entries[key] || {};
      if (isBeforeCutoff(m.month, m.year) || isPartialMonth(m.month, m.year)) {
        training += entry.training_expenses || 0;
        hrSalary += entry.hr_salary_expenses || 0;
        hrOther += entry.hr_other_expenses || 0;
      }
    });
    
    return { training, hrSalary, hrOther };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="historical-data-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-indigo-600" />
            Historical Data Entry
          </h1>
          <p className="text-slate-500 mt-1">Enter expense totals for FY {selectedFY} before system go-live</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => { setTempCutoffDate(cutoffDate); setSettingsModalOpen(true); }}
            data-testid="cutoff-settings-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Cutoff: {cutoffDate || 'Not Set'}
          </Button>
          {canEdit && (
            <Button
              onClick={saveEntries}
              disabled={saving || !hasChanges}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-historical-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save All
            </Button>
          )}
        </div>
      </div>

      {/* Cutoff Info */}
      {!cutoffDate && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Cutoff Date Not Set</p>
              <p className="text-sm text-amber-700">Please set a cutoff date to define which months require historical entry.</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => { setTempCutoffDate(''); setSettingsModalOpen(true); }}
            >
              Set Cutoff Date
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-indigo-600">Training (Historical)</p>
            <p className="text-2xl font-bold text-indigo-700">{formatCurrency(totals.training)}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-blue-600">HR Salary (Historical)</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(totals.hrSalary)}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-purple-600">HR Other (Historical)</p>
            <p className="text-2xl font-bold text-purple-700">{formatCurrency(totals.hrOther)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Entry Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Monthly Data Entry - FY {selectedFY}
          </CardTitle>
          <CardDescription>
            Enter expense totals for each month. Months after cutoff date are system-calculated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-semibold text-slate-600">Month</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Status</th>
                  <th className="text-right p-3 font-semibold text-indigo-600">Training Expenses</th>
                  <th className="text-right p-3 font-semibold text-blue-600">HR (Salary)</th>
                  <th className="text-right p-3 font-semibold text-purple-600">HR (Other)</th>
                </tr>
              </thead>
              <tbody>
                {FY_MONTHS.map((m, idx) => {
                  const key = `${m.year}-${m.month}`;
                  const entry = entries[key] || {};
                  const isBefore = isBeforeCutoff(m.month, m.year);
                  const isPartial = isPartialMonth(m.month, m.year);
                  const isEditable = cutoffDate && (isBefore || isPartial);
                  
                  return (
                    <tr 
                      key={key} 
                      className={`border-b border-slate-100 ${
                        isEditable ? 'bg-white' : 'bg-slate-50/50'
                      }`}
                    >
                      <td className="p-3 font-medium text-slate-900">
                        {m.label} {m.year}
                      </td>
                      <td className="p-3">
                        {isBefore ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Clock className="w-3 h-3 mr-1" />
                            Historical
                          </Badge>
                        ) : isPartial ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Partial
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            System
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditable && canEdit ? (
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                            <Input
                              type="number"
                              value={entry.training_expenses || ''}
                              onChange={(e) => updateEntry(key, 'training_expenses', e.target.value)}
                              className="text-right pl-5 h-8"
                              placeholder="0"
                            />
                          </div>
                        ) : (
                          <p className="text-right font-mono text-slate-400">—</p>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditable && canEdit ? (
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                            <Input
                              type="number"
                              value={entry.hr_salary_expenses || ''}
                              onChange={(e) => updateEntry(key, 'hr_salary_expenses', e.target.value)}
                              className="text-right pl-5 h-8"
                              placeholder="0"
                            />
                          </div>
                        ) : (
                          <p className="text-right font-mono text-slate-400">—</p>
                        )}
                      </td>
                      <td className="p-3">
                        {isEditable && canEdit ? (
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                            <Input
                              type="number"
                              value={entry.hr_other_expenses || ''}
                              onChange={(e) => updateEntry(key, 'hr_other_expenses', e.target.value)}
                              className="text-right pl-5 h-8"
                              placeholder="0"
                            />
                          </div>
                        ) : (
                          <p className="text-right font-mono text-slate-400">—</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Totals Row */}
                <tr className="bg-indigo-50 font-semibold">
                  <td className="p-3 text-indigo-900">Total (Historical)</td>
                  <td className="p-3"></td>
                  <td className="p-3 text-right font-mono text-indigo-900">{formatCurrency(totals.training)}</td>
                  <td className="p-3 text-right font-mono text-blue-900">{formatCurrency(totals.hrSalary)}</td>
                  <td className="p-3 text-right font-mono text-purple-900">{formatCurrency(totals.hrOther)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Cutoff Date Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Set Cutoff Date
            </DialogTitle>
            <DialogDescription>
              Set the date from which system-calculated data should be used.
              Expenses before this date will use your manual historical entries.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cutoff-date">Cutoff Date</Label>
            <Input
              id="cutoff-date"
              type="date"
              value={tempCutoffDate}
              onChange={(e) => setTempCutoffDate(e.target.value)}
              className="mt-2"
              data-testid="cutoff-date-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Example: If you set Feb 15, 2026, then Feb 1-14 uses historical entry, 
              and Feb 15+ uses system data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveCutoffDate}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!tempCutoffDate}
            >
              Save Cutoff Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoricalDataPage;
